import type { AuthProvider } from "@/lib/auth";
import type { ApiClient } from "./types";

export interface ApiClientOptions {
  auth: AuthProvider;
  baseUrl?: string;
  apiPrefix?: string;
  fetchImpl?: typeof fetch;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  const baseUrl = opts.baseUrl;
  const apiPrefix = opts.apiPrefix;

  return {
    fetch(input, init) {
      const headers = new Headers(init?.headers);
      const token = opts.auth.state.accessToken ?? opts.auth.state.idToken;

      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      if (!headers.has("Accept")) {
        headers.set("Accept", "application/json");
      }

      return fetchImpl(resolveUrl(input, baseUrl, apiPrefix), {
        ...init,
        headers,
      });
    },
  };
}

function resolveUrl(input: string | URL, baseUrl?: string, apiPrefix?: string): string | URL {
  if (input instanceof URL) return input;
  if (isAbsoluteUrl(input)) return input;
  if (isFullApiPath(input)) {
    return baseUrl ? new URL(input.replace(/^\/+/, ""), normalizedBaseUrl(baseUrl)) : input;
  }
  const path = joinPath(apiPrefix, input);
  if (!baseUrl) return apiPrefix ? `/${path}` : input;
  return new URL(path, normalizedBaseUrl(baseUrl));
}

function isAbsoluteUrl(input: string): boolean {
  return /^[a-z][a-z\d+\-.]*:/i.test(input);
}

function isFullApiPath(input: string): boolean {
  return /^\/api(?:\/|$)/.test(input);
}

function normalizedBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
}

function joinPath(prefix: string | undefined, path: string): string {
  return [prefix, path]
    .filter((part): part is string => Boolean(part))
    .map((part) => part.replace(/^\/+|\/+$/g, ""))
    .filter(Boolean)
    .join("/");
}
